import { RuntimeConfig } from "@/types/types.ts";
import styles from "./DeliveryServices.module.scss";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import { DeliveryServiceItem } from "./components/DeliveryServiceItem";

import {
  SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
  SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED,
} from "@/constants/constants.ts";
import { removeDmsPrefix } from "@/utils/services/emitServices.ts";
import { Services, TransformedDeliveryService } from "@/types/serviceTypes.ts";
import { SharedServicesActions } from "@/utils/services/useSharedServices.ts";
import { ServiceQuestionnaireResult } from "@/components/DeliveryServices/types.ts";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import {
  recordImpressionByParam,
  sendEvent,
} from "@JohnLewisPartnership/experiments-utils";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { useEffect } from "react";

type Props = {
  availableToOrder: boolean;
  deliveryServiceQuestionnaireEnabled: boolean;
  showFreeServicePromoTag: boolean;
  experiencesRawObject: Experiences;
  runtimeConfig: RuntimeConfig;
  availableServices: Services;
  actions: SharedServicesActions;
};

export const DeliveryServicesOptions = ({
  availableToOrder,
  deliveryServiceQuestionnaireEnabled,
  showFreeServicePromoTag,
  experiencesRawObject,
  runtimeConfig,
  availableServices,
  actions,
}: Props) => {
  const { monetateId } = useMonetateId();

  const deliveryServices: TransformedDeliveryService[] =
    availableServices.filter(
      (service) => service.serviceType === "DeliveryService",
    );

  const hasMandatoryServices = deliveryServices.some(
    (service) => service.isMandatory,
  );

  const onToggle = (service: TransformedDeliveryService) => {
    const { id, skuId, selected } = service;
    if (deliveryServiceQuestionnaireEnabled && !selected) {
      openServiceQuestionnaire(
        skuId,
        id,
        monetateId,
        experiencesRawObject,
        runtimeConfig,
      );
    } else if (selected) {
      actions.removeService(id);
    } else {
      actions.selectService(id);
    }
  };

  const handleDeliveryServiceQuestionnaireCompletion = (
    event: CustomEvent<ServiceQuestionnaireResult>,
  ) => {
    const { serviceId, success } = event.detail;

    sendEvent(
      success
        ? "wpdp-service-questionnaire-survey-success"
        : "wpdp-service-questionnaire-survey-rejected",
    );
    if (success) {
      actions.selectService(serviceId);
    }
  };

  useEffect(() => {
    if (deliveryServiceQuestionnaireEnabled) {
      window.addEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    }

    return () => {
      window.removeEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    };
  }, [deliveryServiceQuestionnaireEnabled]);

  if (!deliveryServices.length) return null;

  return (
    <div data-testid="product:delivery-services" className={styles.services}>
      <h2 className={styles.title}>Installation & Disposal</h2>
      <OptionList isButtonList={false}>
        {deliveryServices
          .filter((service) => !service.isMandatory)
          .map((service) => {
            return (
              <DeliveryServiceItem
                checked={service.selected}
                title={service.title}
                key={service.skuId}
                serviceId={service.id}
                description={service.description}
                display={service.display}
                value={service.value}
                toggleCheck={() => onToggle(service)}
                disabled={!availableToOrder || service.isMandatory}
                showFreeServicePromoTag={showFreeServicePromoTag}
              />
            );
          })}
      </OptionList>
      {hasMandatoryServices && (
        <DEPRECATED_Message
          type="information"
          body="Free installation is included with this American fridge freezer"
        />
      )}
    </div>
  );
};

function openServiceQuestionnaire(
  skuId: string,
  serviceId: string,
  monetateId: string,
  experiencesRawObject: Experiences,
  runtimeConfig: RuntimeConfig,
) {
  const dmsId = removeDmsPrefix(skuId);
  const checkForGas = ["3913", "6901", "3905", "8301", "3911", "3908"];

  let payload;
  if (checkForGas.includes(dmsId)) {
    payload = {
      services: [
        {
          dmsId: dmsId,
          serviceId: serviceId,
        },
        {
          dmsId: dmsId,
          serviceId: serviceId,
        },
      ],
      parentItemId: "not-required-for-pdp",
      invokedBy: "pdp",
    };
  } else {
    payload = {
      dmsId: dmsId,
      serviceId: serviceId,
      parentItemId: "not-required-for-pdp",
      invokedBy: "pdp",
    };
  }

  recordImpressionByParam(
    runtimeConfig?.monetateEnvironment,
    experiencesRawObject,
    "pdp-scaffold-observeServiceQuestionnaire",
    "true",
    monetateId,
  );

  window.dispatchEvent(
    new CustomEvent(SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED, {
      detail: payload,
    }),
  );
}
